import { useEffect, useContext, useCallback, useMemo, startTransition } from "react";
import { AuthorizationContext, PERMISSION, DOMAIN } from "@/providers/AuthorizationProvider";
import { Path, useNavigate } from "@/router";
import { ROLE } from "@/providers/AuthorizationProvider/types";
export { DOMAIN, PERMISSION } from "@/providers/AuthorizationProvider";

export interface Capability {
  domains: DOMAIN[];
  permissions: PERMISSION[];
}

const useCapabilities = () => useContext(AuthorizationContext);

/**
 * Hook to deny authorization and redirect to home page.
 * Extracting so we can react to unauthorized routes consistently.
 */
export function useDenyAuthorization() {
  const navigate = useNavigate();

  return useCallback(
    (path: Path = "/") => {
      // Not sure how, but this relieves a 'Maximum update depth exceeded' warning.
      startTransition(() => {
        // redirect user to home. At some point we will need a modal explaining the issue.
        navigate(path);
      });
    },
    [navigate]
  );
}

/**
 * Utility function to check if a capability is present.
 */
const hasCapability = (capabilities: Capability, domain: DOMAIN, permission: PERMISSION): boolean => {
  const { domains, permissions } = capabilities;
  return domains.includes(domain) && permissions.includes(permission);
};

export const useRequireCapability = ({ domain, permission }: { domain: DOMAIN; permission: PERMISSION }) => {
  const { loaded, capabilities } = useCapabilities();
  const denyAuthorization = useDenyAuthorization();

  useEffect(() => {
    const { domains, permissions } = capabilities;
    if (loaded && (domains.length === 0 || !domains.includes(domain) || !permissions.includes(permission))) {
      denyAuthorization();
    }
  }, [loaded, capabilities, domain, permission, denyAuthorization]);
};

/**
 * Hook for checking multiple permissions
 *
 * Example usage:
 * const canView = useRequireCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPeople });
 */
export const useHasCapabilityBool = ({ domain, permission }: { domain: DOMAIN; permission: PERMISSION }): boolean => {
  const { loaded, capabilities } = useCapabilities();

  return useMemo(() => {
    return loaded && hasCapability(capabilities, domain, permission);
  }, [loaded, capabilities, domain, permission]);
};

/**
 * Predefined permission check hooks.
 */
export const useCanViewInteractionsBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPeopleInteractions });

export const useCanManageInteractionsBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.managePeopleInteractions });

export const useCanViewPersonContactBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPersonContact });

export const useCanViewPersonMembershipBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPersonMembership });

export const useCanViewPersonSuspensionHistoryBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPersonSuspensionHistory });

export const useCanViewPersonEducationBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPersonEducation });

export const useCanViewPersonCareerBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPersonCareer });

export const useCanViewPersonEventsBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPersonEvents });

export const useCanViewPersonBillingBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPersonBilling });

export const useCanViewPersonGroupsBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPersonGroups });

export const useCanViewPersonNotesBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPersonNotes });

export const useCanViewPersonChangeHistoryBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPersonChangeHistory });

export const useCanViewPersonDocumentsBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPersonDocuments });

export const useCanViewPersonInsightsBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPersonInsights });

export const useCanViewOrdersBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.COMMERCE, permission: PERMISSION.viewOrders });

export const useCanViewJobPostsBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.JOBS, permission: PERMISSION.viewJobPosts });

export const useCanManagePeople = () =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.managePeople });

export const useCanManagePersonNotes = () => useCanManagePeople();

export const useCanManageFacilities = () =>
  useHasCapabilityBool({ domain: DOMAIN.FACILITIES, permission: PERMISSION.manageFacilities });

export const useCanViewFacilities = () =>
  useHasCapabilityBool({ domain: DOMAIN.FACILITIES, permission: PERMISSION.viewFacilities });

export const useCanManageJobPostsBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.JOBS, permission: PERMISSION.manageJobPosts });

export const useCanViewCareerServicesDashboardBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewCareerServicesDashboard });

export const useCanViewCareerPreferencesBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewCareerPreferences });

export const useCanViewCareerPlanBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewCareerPlan });

export const useCanManageCareerPlanBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.manageCareerPlan });

export const useCanManageCareerPreferencesBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.manageCareerPreferences });

export const useCanManageAchievementsBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.ACHIEVEMENTS, permission: PERMISSION.manageAchievements });

export const useCanViewCompensationProfilesBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewCompensationProfiles });

export const useCanViewRecruitingEventRecapsBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.EVENTS, permission: PERMISSION.viewRecruitingEventRecaps });

export const useCanManageEmployments = () =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.manageEmployments });

export const useCanManageNonMemberEmployments = () =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.manageNonMemberEmployments });

export const useCanManageMemberBands = () =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.manageMemberBands });

export const useCanDeleteMemberBands = () =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.deleteMemberBands });

export const useCanManageChapters = () =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.manageChapters });

export const useCanManageSuspensions = () =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.manageSuspensions });

export const useCanManageSuspensionsRestricted = () =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.manageSuspensionsRestricted });

export const useCanEditEmail = () => useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.editEmail });

export const useCanEditLastName = () =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.editLastName });

export const useCanViewBeneficiariesBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewBeneficiaries });

export const useCanManageBeneficiariesBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.manageBeneficiaries });

export const useCanViewGrpCommissionBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.BENEFIT_PROGRAMS, permission: PERMISSION.viewGrpCommission });

export const useCanManageGrpCommissionBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.BENEFIT_PROGRAMS, permission: PERMISSION.manageGrpCommission });

export const useCanViewTasksBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.TASKS, permission: PERMISSION.viewTasks });

export const useCanManageTasksBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.TASKS, permission: PERMISSION.manageTasks });

export const useCanManageTaskNotificationPreferencesBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.TASKS, permission: PERMISSION.manageTaskNotificationPreferences });

export const useCanViewCommitteesBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewCommittees });

export const useCanManageCommitteesBool = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.manageCommittees });

/**
 * National Section committee catalog Group Email (not section Operations committees).
 * Prefer MeQuery `sendHQGroupEmail` (pgahq/pga-backend); until that ships, MS Admin and global admin still qualify via role.
 */
export const useCanSendNationalCommitteeGroupEmailBool = (): boolean => {
  const { loaded, hasRole } = useCapabilities();
  const hasSendHQGroupEmailCapability = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.sendHQGroupEmail
  });
  return useMemo(
    () =>
      loaded &&
      (hasSendHQGroupEmailCapability || hasRole(ROLE.GLOBAL_ADMIN) || hasRole(ROLE.MEMBERSHIP_SERVICES_ADMIN)),
    [loaded, hasSendHQGroupEmailCapability, hasRole]
  );
};

export const useCanManageApplications = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.manageApplications });

export const useCanViewPartialBackgroundCheck = (): boolean => {
  const canViewPartial = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPartialBackgroundCheck
  });
  const canViewFull = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewFullBackgroundCheck
  });

  return canViewPartial || canViewFull;
};

export const useCanViewFullBackgroundCheck = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewFullBackgroundCheck });

export const useCanManageBackgroundCheck = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.manageBackgroundCheck });

export const useCanCreateBackgroundCheckOrder = (): boolean =>
  useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.createBackgroundCheckOrder });

export const useCanViewOperationsTabBool = (): boolean => true;

export const useCanAdminPublishJob = (): boolean => {
  const { hasRole } = useCapabilities();
  return useMemo(() => hasRole(ROLE.CAREER_SERVICES_ADMIN) || hasRole(ROLE.GLOBAL_ADMIN), [hasRole]);
};

export const useIsCareerServicesBool = (): boolean => {
  const { hasRole } = useCapabilities();
  return useMemo(
    () => hasRole(ROLE.CAREER_SERVICES_STAFF) || hasRole(ROLE.CAREER_SERVICES_ADMIN) || hasRole(ROLE.GLOBAL_ADMIN),
    [hasRole]
  );
};

/**
 * Hook for checking photobooth access
 * Requires one of: GLOBAL_ADMIN, MEMBERSHIP_SERVICES_STAFF, MEMBERSHIP_SERVICES_ADMIN
 */
export const useCanAccessPhotoboothBool = (): boolean => {
  const { loaded, hasRole } = useCapabilities();
  return useMemo(
    () =>
      loaded &&
      (hasRole(ROLE.GLOBAL_ADMIN) ||
        hasRole(ROLE.MEMBERSHIP_SERVICES_STAFF) ||
        hasRole(ROLE.MEMBERSHIP_SERVICES_ADMIN)),
    [loaded, hasRole]
  );
};

/**
 * Hook for checking multiple roles
 *
 * Example usage:
 * const canView = useHasRoles([ROLE.TECHNOLOGY_STAFF, ROLE.GLOBAL_ADMIN]);
 */
export const useHasRoles = (roles: string[]) => {
  const { hasRole } = useCapabilities();
  return roles.some((role) => hasRole(role as ROLE));
};

export default useCapabilities;
